/**
 * Cruce de podios — lógica pura (port de podios-stories/cruce.py).
 *
 * Dado un resultado del scraper (atleta+prueba+marca) y un contexto ya
 * preparado (historial PB/SB, mínimas, récord, ranking) decide qué flags
 * lo hacen relevante y qué etiquetas pintar en el vídeo/imagen.
 *
 * Portado 1:1 desde cruce.py para mantener paridad con la app Flask
 * original. La capa de datos (podios/data.ts) prepara el `ctx` desde los
 * aggregates servidos por Hosting (agg_pbsb_*, recordsEspana, RK200,
 * minimas_internacional) y llama a analizarResultado() por cada atleta.
 */
import { stripDiacritics } from '@/lib/normalize';

const SIN_MARCA = new Set(['DNS', 'DNF', 'DQ', 'DNQ', 'NM', 'NULO', 'ELIM', 'AB', 'SR', '-', '']);
const LIMITE_VIENTO = 2.0;
const PAIS_ESPANA = new Set(['spain', 'espana', 'esp', 'es']);
const CAMPO = [
  'longitud', 'triple', 'altura', 'pertiga', 'peso', 'disco', 'martillo',
  'jabalina', 'heptatl', 'decatl', 'pentatl', 'hexatl', 'combinad',
];

/** minúsculas, sin acentos, sin signos → para comparar nombres/pruebas. */
export function normaliza(s: string | null | undefined): string {
  if (!s) return '';
  return stripDiacritics(String(s).toLowerCase())
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Reduce un nombre normalizado a 'inicial + apellido(s)'. */
export function claveInicial(nombreNormalizado: string): string {
  const toks = (nombreNormalizado || '').split(' ').filter(Boolean);
  if (toks.length === 0) return '';
  if (toks.length === 1) return toks[0];
  return toks[0][0] + ' ' + toks.slice(1).join(' ');
}

/** Todas las hipótesis de corte nombre/apellido (para nombres compuestos). */
export function clavesIniciales(nombreNormalizado: string): Set<string> {
  const toks = (nombreNormalizado || '').split(' ').filter(Boolean);
  const claves = new Set<string>();
  if (toks.length === 0) return claves;
  claves.add(toks[0]);
  for (const corte of [1, 2, 3]) {
    const resto = toks.slice(corte);
    if (resto.length) claves.add(toks[0][0] + ' ' + resto.join(' '));
    else break;
  }
  return claves;
}

/** Marca → número comparable (segundos para tiempos, metros/puntos para
 *  concursos). null si no es válida (DNS, DNF…). */
export function valorMarca(m: string | null | undefined): number | null {
  if (m == null) return null;
  let t = String(m).trim().replace(',', '.');
  if (SIN_MARCA.has(t.toUpperCase())) return null;
  const parts = t.split(' ');
  t = parts.length ? parts[0] : t; // quita viento/código de récord pegado
  let mm = t.match(/^(\d+):(\d{1,2}):(\d{1,2}(?:\.\d+)?)$/);
  if (mm) return parseInt(mm[1]) * 3600 + parseInt(mm[2]) * 60 + parseFloat(mm[3]);
  mm = t.match(/^(\d+):(\d{1,2}(?:\.\d+)?)$/);
  if (mm) return parseInt(mm[1]) * 60 + parseFloat(mm[2]);
  // Puntos de pruebas COMBINADAS (pent/hept/deca/hexatlón, triatlón…) con
  // separador de MILLAR: "8.214" / "8,214" valen 8214, no 8,214. Solo los puntos
  // llevan grupos de EXACTAMENTE 3 dígitos tras el separador (las marcas
  // normales tienen ≤2 decimales), así que el patrón es inequívoco. Antes de
  // esto el all-time / R.Esp de combinadas no cruzaba (Riki 2026-07-14).
  if (/^\d{1,3}(?:\.\d{3})+$/.test(t)) return parseInt(t.replace(/\./g, ''), 10);
  if (/^\d+(?:\.\d+)?$/.test(t)) return parseFloat(t);
  return null;
}

function valorViento(s: string | null | undefined): number | null {
  if (s == null) return null;
  const t = String(s).trim().replace(',', '.');
  if (!t) return null;
  const v = parseFloat(t);
  return Number.isFinite(v) ? v : null;
}

/** True si viento favorable supera +2.0 m/s (anula marca). */
export function vientoAnula(s: string | null | undefined): boolean {
  const v = valorViento(s);
  return v !== null && v > LIMITE_VIENTO;
}

/** True si el país NO es España (extranjero). Sin dato → no extranjero. */
export function esExtranjero(pais: string | null | undefined): boolean {
  const p = normaliza(pais);
  if (!p) return false;
  return !PAIS_ESPANA.has(p);
}

/** True si en la prueba MÁS es MEJOR (saltos, lanzamientos, combinadas). */
export function esConcurso(prueba: string): boolean {
  const p = normaliza(prueba);
  return CAMPO.some((k) => p.includes(k));
}

/** True si la prueba es una COMBINADA (pent/hept/deca/hexatlón, triatlón…):
 *  su "marca" son PUNTOS con separador de millar (Riki 2026-07-14). */
export function esCombinada(prueba: string): boolean {
  return /atlon/.test(normaliza(prueba));
}

/** True si la marca 'a' es mejor que 'b'. */
export function mejor(a: number | null, b: number | null | undefined, concurso: boolean): boolean {
  if (b == null) return true;
  if (a == null) return false;
  return concurso ? a > b : a < b;
}

export function cumple(marcaVal: number | null, minimaVal: number | null, concurso: boolean): boolean {
  if (marcaVal == null || minimaVal == null) return false;
  return concurso ? marcaVal >= minimaVal : marcaVal <= minimaVal;
}

function detectaSexo(prueba: string): 'H' | 'M' | null {
  const toks = new Set(normaliza(prueba).split(' '));
  if (['mujeres', 'mujer', 'femenino', 'fem'].some((t) => toks.has(t))) return 'M';
  if (['hombres', 'hombre', 'masculino', 'masc', 'varones'].some((t) => toks.has(t))) return 'H';
  return null;
}

export interface SignaturaPrueba {
  base: string;
  spec: string;
  sexo: 'H' | 'M' | null;
  concurso: boolean;
}

/** Clave canónica de una prueba (base sin sexo/ronda/spec). */
export function signaturaPrueba(prueba: string): SignaturaPrueba {
  let spec = '';
  const msp = prueba.match(/\(([^)]*)\)/);
  if (msp) spec = normaliza(msp[1]);
  let baseN = normaliza(prueba.replace(/\([^)]*\)/g, ' '));
  const quita = [
    'hombres', 'mujeres', 'masculino', 'femenino', 'masc', 'fem', 'hombre',
    'mujer', 'varones', 'final', 'semifinal', 'serie', 'cuartos',
    'eliminatoria', 'repesca',
  ];
  for (const w of quita) baseN = baseN.replace(new RegExp(`\\b${w}\\b`, 'g'), ' ');
  baseN = baseN.replace(/\b[abc]\b/g, ' ').replace(/\s+/g, ' ').trim();
  return { base: baseN, spec, sexo: detectaSexo(prueba), concurso: esConcurso(prueba) };
}

/** Categoría por edad en la temporada (bandas RFEA aprox.). */
export function categoriaPorEdad(anioNac: number | null, anioTemporada: number): string | null {
  if (!anioNac) return null;
  const e = anioTemporada - anioNac;
  if (e <= 9) return 'Sub 10';
  if (e <= 11) return 'Sub 12';
  if (e <= 13) return 'Sub 14';
  if (e <= 15) return 'Sub 16';
  if (e <= 17) return 'Sub 18';
  if (e <= 19) return 'Sub 20';
  if (e <= 22) return 'Sub 23';
  if (e <= 34) return 'Absoluta';
  return 'Master';
}

export function anioDeFecha(s: string | null | undefined): number | null {
  if (!s) return null;
  const m = String(s).match(/(\d{4})/);
  return m ? parseInt(m[1]) : null;
}

function mejorDe(valores: (number | null)[], concurso: boolean): number | null {
  let best: number | null = null;
  for (const x of valores || []) {
    if (x != null && (best === null || mejor(x, best, concurso))) best = x;
  }
  return best;
}

/** Flag simple (string) o tupla [tipo, label] para MINIMA/MINIMA_REPESCA. */
export type Flag = string | [string, string];

export interface MinimaCtx {
  minima: string | null;
  minimaRepesca: string | null;
  label: string;
}

export interface CruceCtx {
  temporada: number;
  historial: number[];
  historialTemporada: number[];
  minimas: MinimaCtx[];
  recordValor: number | null;
  rankingMejorAbs: number | null;
  rankingMejorCat: number | null;
  /** true si la carga semanal de la BD es POSTERIOR a la competición (sus
   *  marcas ya están dentro). Cambia la interpretación de los empates: ver
   *  analizarResultado. Calculado en data.ts (fecha evento vs generatedAt). */
  bdIncluyeCompeticion?: boolean;
}

export interface ResultadoInput {
  atleta?: string | null;
  prueba: string;
  marca?: string | null;
  viento?: string | null;
  pais?: string | null;
  fechaNac?: string | null;
}

export interface CruceOut {
  atleta: string | null | undefined;
  prueba: string;
  marca: string | null | undefined;
  marcaValida: boolean;
  viento: string | null | undefined;
  vientoAnula: boolean;
  pais: string | null | undefined;
  extranjero: boolean;
  concurso: boolean;
  flags: Flag[];
  categoriaEdad?: string | null;
}

/** Port de cruce.analizar_resultado(). */
export function analizarResultado(res: ResultadoInput, ctx: CruceCtx): CruceOut {
  const concurso = esConcurso(res.prueba || '');
  const v = valorMarca(res.marca);
  const viento = res.viento;
  const anulada = vientoAnula(viento);
  const extranjero = esExtranjero(res.pais);
  const flags: Flag[] = [];
  const out: CruceOut = {
    atleta: res.atleta, prueba: res.prueba, marca: res.marca,
    marcaValida: v !== null, viento, vientoAnula: anulada,
    pais: res.pais, extranjero, concurso, flags,
  };
  if (v === null) return out;
  if (anulada) return out;

  // MMP / mejor temporada (solo si hay histórico). Empate → depende de si
  // la BD ya incluye esta competición (regla de Riki 2026-07-08: hay que
  // comparar contra el historial ANTERIOR al día de la competición; los
  // agg_pbsb no llevan fecha por marca, así que se aproxima a nivel de
  // competición): si la carga semanal es posterior al evento, "igualar el
  // PB/SB" significa casi seguro que el PB/SB ES la marca de este día →
  // cuenta como PB/SB real. Si la BD es anterior (competición en directo),
  // el empate es un empate de verdad → =PB/=SB. Caso no decidible sin
  // fechas por marca: igualar HOY un PB antiguo en una competición ya
  // cargada saldrá como PB (se asume el caso más probable).
  const yaEnBD = ctx.bdIncluyeCompeticion === true;
  const hist = ctx.historial || [];
  const mejorHist = mejorDe(hist, concurso);
  if (hist.length && mejor(v, mejorHist, concurso)) flags.push('MMP');
  else if (hist.length && mejorHist !== null && v === mejorHist) flags.push(yaEnBD ? 'MMP' : 'IGUALA_MMP');

  const histT = ctx.historialTemporada || [];
  const mejorHistT = mejorDe(histT, concurso);
  if (histT.length && mejor(v, mejorHistT, concurso)) {
    if (!flags.includes('MMP')) flags.push('MEJOR_TEMPORADA');
  } else if (histT.length && mejorHistT !== null && v === mejorHistT) {
    if (!flags.includes('MMP') && !flags.includes('IGUALA_MMP')) flags.push(yaEnBD ? 'MEJOR_TEMPORADA' : 'IGUALA_SB');
  }

  // Mínimas internacionales: se evalúan DETRÁS del GATE (más abajo), no aquí
  // (decisión Riki 2026-07-21, revierte la del 07-11). Solo son noticia si la
  // marca de HOY es PB/SB; repetir la mínima sin mejorar no saca píldora.

  // GATE: sin mejora no se calculan los flags de récord/líder. A DIFERENCIA
  // del cruce Python local, aquí los EMPATES (IGUALA_MMP/IGUALA_SB) también
  // pasan: los datasets del front se regeneran semanalmente SIN fecha por
  // marca, así que si esta competición ya entró en la carga, la marca
  // "iguala su PB" precisamente porque su PB ES esta marca (caso real
  // 2026-07-08 con Riki: récord de España sub-18 de 110 m vallas invisible
  // porque la BD ya traía el 13.36 de la propia final). cruce_local.py no
  // necesita esto: compara contra el historial ANTERIOR a la fecha de la
  // competición (marcas_fecha / _historial_antes_de).
  if (!flags.some((f) => f === 'MMP' || f === 'MEJOR_TEMPORADA' || f === 'IGUALA_MMP' || f === 'IGUALA_SB')) return out;

  // Hitos ligados a nacionalidad española: extranjero no puede.
  if (extranjero) return out;

  // Mínimas internacionales (detrás del GATE: solo con PB/SB de hoy). Por cada
  // mínima que la marca de HOY cumpla se distingue si es NUEVA o una MEJORA de
  // una que YA se tenía: se tenía si el SB ANTERIOR a hoy (historialTemporada,
  // que en directo aún no incluye la carrera) ya la cumplía. Así una primera
  // mínima puede lograrse con PB o con SB, y varias mínimas distintas sacan
  // varias píldoras (cada una nueva o mejora por separado). Riki 2026-07-21.
  const sbPrevio = mejorDe(ctx.historialTemporada || [], concurso);
  for (const mn of ctx.minimas || []) {
    const minVal = valorMarca(mn.minima);
    if (cumple(v, minVal, concurso)) {
      const yaLaTenia = cumple(sbPrevio, minVal, concurso);
      flags.push([yaLaTenia ? 'MEJORA_MINIMA' : 'MINIMA', mn.label || 'mínima']);
    } else if (cumple(v, valorMarca(mn.minimaRepesca), concurso)) {
      flags.push(['MINIMA_REPESCA', mn.label || 'repesca']);
    }
  }

  // Récord de España: batirlo O IGUALARLO. La igualdad cubre dos casos
  // reales: (a) igualar el récord vigente es noticia por sí misma, y
  // (b) recordsEspana.json puede venir ya actualizado con la marca de ESTA
  // competición (el récord del propio atleta) — sin la igualdad, cargar la
  // competición tras la actualización dejaría de mostrar su récord.
  if (ctx.recordValor !== null && (mejor(v, ctx.recordValor, concurso) || v === ctx.recordValor)) flags.push('RECORD_ESPANA');
  // Líder del ranking: batir el mejor valor o, si la BD ya incluye esta
  // competición, IGUALARLO (el "mejor del ranking" es casi seguro esta
  // misma marca — mismo razonamiento que los empates de PB/SB).
  const lidera = (mejorRk: number | null): boolean =>
    mejorRk !== null && (mejor(v, mejorRk, concurso) || (yaEnBD && v === mejorRk));
  if (lidera(ctx.rankingMejorAbs)) flags.push('N1_ABSOLUTO');
  if (lidera(ctx.rankingMejorCat)) flags.push('N1_CATEGORIA');

  return out;
}

// ───────────────────────── etiquetas para el vídeo ─────────────────────────
const SUFIJO_CATEGORIA: Record<string, string> = {
  'Sub 10': 'U10', 'Sub 12': 'U12', 'Sub 14': 'U14', 'Sub 16': 'U16',
  'Sub 18': 'U18', 'Sub 20': 'U20', 'Sub 23': 'U23', 'Absoluta': '', 'Master': 'Mas',
};

function abreviaMinima(labelCompleto: string): string {
  const low = (labelCompleto || '').toLowerCase();
  let ambito: string;
  if (low.includes('mun') || low.includes('world')) ambito = 'Mun';
  else if (low.includes('eur')) ambito = 'Eur';
  else {
    const primera = labelCompleto ? labelCompleto.split(' ')[0] : '';
    ambito = primera.slice(0, 3).replace(/^./, (c) => c.toUpperCase()) || 'Int';
  }
  let suf = '';
  for (const [catLarga, catCorta] of Object.entries(SUFIJO_CATEGORIA)) {
    if (labelCompleto && labelCompleto.endsWith(catLarga)) { suf = catCorta; break; }
  }
  return `Mín.${ambito} ${suf}`.trim();
}

/** Port de cruce.etiquetas_video(): jerarquía de píldoras a pintar.
 *  Jerarquía 2026-07-07 ("el récord engloba todo", igual que cruce.py):
 *  1. Récord de España -> SOLO "R.Esp". 2. Líder (sin récord) -> "LE" + PB/SB.
 *  3. Mínima(s) (sin récord ni líder) -> "Mín.X" + PB/SB. 4./5. PB o SB solos
 *  (=PB/=SB siempre salen solos por el GATE de analizarResultado). */
export function etiquetasVideo(out: CruceOut): string[] {
  const flags = out.flags || [];
  const cat = out.categoriaEdad;
  const suf = cat ? SUFIJO_CATEGORIA[cat] ?? '' : '';
  const has = (f: string) => flags.includes(f);

  // Píldoras de mínima: INDEPENDIENTES (Riki 2026-07-21). Salen siempre que
  // apliquen, aun con récord/all-time/líder — son información distinta
  // (clasificar a un campeonato). "Mín.X" = mínima nueva; "Mejora Mín.X" =
  // mejora de una que ya se tenía (mismo color). Varias mínimas → varias
  // píldoras.
  const minimaPills: string[] = [];
  for (const f of flags) {
    if (Array.isArray(f) && f[0] === 'MINIMA') minimaPills.push(abreviaMinima(f[1]));
    else if (Array.isArray(f) && f[0] === 'MEJORA_MINIMA') minimaPills.push('Mejora ' + abreviaMinima(f[1]));
    else if (Array.isArray(f) && f[0] === 'MINIMA_REPESCA') minimaPills.push(abreviaMinima(f[1]) + ' (repesca)');
  }

  // 1. Récord de España: implica ser #1 all-time y haber mejorado (PB), así que
  //    NO se añaden all-time, LE ni PB/SB. Pero SÍ las mínimas (independientes).
  if (has('RECORD_ESPANA')) return [`R.Esp ${suf}`.trim(), ...minimaPills];

  const etiquetas: string[] = [];

  // 2. Top-10 histórico (all-time); si no, líder español (absoluto implica el
  //    de su categoría). Estos sí se excluyen entre sí por jerarquía.
  const alltime = flags.find((f): f is [string, string] => Array.isArray(f) && f[0] === 'ALLTIME');
  if (alltime) etiquetas.push(`#${alltime[1]} all-time`);
  else if (has('N1_ABSOLUTO')) etiquetas.push('LE');
  else if (has('N1_CATEGORIA')) etiquetas.push(`LE ${suf}`.trim());

  // 3. Mínima(s): independientes, se añaden aunque haya all-time o líder.
  etiquetas.push(...minimaPills);

  // 4. La marca al final ("LE · PB", "Mín.Eur · SB", o sola). Los empates se
  // pintan igual que las mejoras — nada de "=PB"/"=SB" (flags IGUALA_* se
  // conservan por si se quiere volver a distinguir).
  if (has('MMP') || has('IGUALA_MMP')) etiquetas.push('PB');
  else if (has('MEJOR_TEMPORADA') || has('IGUALA_SB')) etiquetas.push('SB');

  return etiquetas;
}
